import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, SimpleGrid, Text, Flex, Icon, Skeleton } from "@chakra-ui/react";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

import { neutral, signal, type, radius, elevation, numeric } from "../../../theme/tokens";

// --- Formatting -----------------------------------------------------------

const formatCurrency = (value) => {
  const num = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (num >= 1_000_000) return `${sign}R${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${sign}R${(num / 1_000).toFixed(0)}K`;
  return `${sign}R${num.toFixed(0)}`;
};

const formatUnits = (value) => {
  const num = Math.abs(value);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return `${num}`;
};

// --- Tile -----------------------------------------------------------------

/**
 * One metric.
 *
 * Deliberately plain: an uppercase label, the number, one line of context.
 * The previous tile counted every value up from zero over 800ms on each load
 * and each supplier switch — so the first thing you saw after asking for a
 * number was a different, wrong number — and staggered six fade-ins behind
 * that. Colour appears only where it means something: on the change indicator.
 */
const Kpi = ({ label, value, sub, delta, tone, isLoading, isLast }) => {
  if (isLoading) {
    return (
      <Box px={4} py={3.5} borderRight={isLast ? "none" : "1px solid"} borderColor={neutral[200]}>
        <Skeleton h="9px" w="60%" mb={2.5} />
        <Skeleton h="22px" w="75%" mb={2} />
        <Skeleton h="9px" w="45%" />
      </Box>
    );
  }

  const deltaTone = delta >= 0 ? signal.up : signal.down;

  return (
    <Box
      px={4}
      py={3.5}
      minW={0}
      borderRight={{ base: "none", md: isLast ? "none" : "1px solid" }}
      borderBottom={{ base: isLast ? "none" : "1px solid", md: "none" }}
      borderColor={neutral[200]}
    >
      <Text
        fontSize={type.size.micro}
        fontWeight={type.weight.semibold}
        letterSpacing={type.tracking.label}
        textTransform="uppercase"
        color={neutral[500]}
        noOfLines={1}
      >
        {label}
      </Text>

      <Flex align="baseline" gap={2} mt={1.5}>
        <Text
          fontSize={type.size.display}
          fontWeight={type.weight.semibold}
          letterSpacing={type.tracking.display}
          lineHeight={1.05}
          color={tone || neutral[900]}
          sx={numeric}
          noOfLines={1}
        >
          {value}
        </Text>

        {delta !== undefined && delta !== null && (
          <Flex align="center" gap="3px" flexShrink={0}>
            <Icon as={delta >= 0 ? FaArrowUp : FaArrowDown} boxSize="9px" color={deltaTone} />
            <Text fontSize={type.size.caption} fontWeight={type.weight.semibold} color={deltaTone} sx={numeric}>
              {Math.abs(delta).toFixed(1)}%
            </Text>
          </Flex>
        )}
      </Flex>

      {sub && (
        <Text fontSize={type.size.caption} color={neutral[400]} mt={1} sx={numeric} noOfLines={1}>
          {sub}
        </Text>
      )}
    </Box>
  );
};

// --- Strip ----------------------------------------------------------------

const KpiCards = ({ supplier, retailer }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/kpi_cards`,
          { supplier, retailer, userid: localStorage.getItem("userid") },
          { signal: controller.signal }
        );
        if (res.data.status === 1) setData(res.data.data);
      } catch (err) {
        if (!axios.isCancel(err)) console.error("Error fetching KPI cards", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchData();
    return () => controller.abort();
  }, [supplier, retailer]);

  const showDiscontinued = data?.sohValueDiscontinued != null;

  // PnP's YoY tile compares month-to-date against the same window last year,
  // so the label says so. Every other retailer keeps the generic wording.
  const isPnp = (retailer || "").toUpperCase() === "PNP";

  const tiles = [
    {
      label: "Sales MTD",
      value: formatCurrency(data?.salesMtd?.amount || 0),
      sub: `${formatUnits(data?.salesMtd?.units || 0)} units`,
    },
    {
      label: "Previous month",
      value: formatCurrency(data?.previousMonth?.amount || 0),
      sub: `${formatUnits(data?.previousMonth?.units || 0)} units`,
    },
    {
      label: isPnp ? "MTD YoY" : "YoY change",
      value: `${(data?.yoyChange?.percentage || 0) > 0 ? "+" : ""}${(data?.yoyChange?.percentage || 0).toFixed(1)}%`,
      sub: `Last year ${formatCurrency(data?.yoyChange?.lastYearAmount || 0)}`,
      delta: data?.yoyChange?.percentage,
      tone: (data?.yoyChange?.percentage || 0) >= 0 ? signal.up : signal.down,
    },
    {
      label: "SOH value",
      value: formatCurrency(data?.sohValue?.value || 0),
      sub: `${formatUnits(data?.sohValue?.units || 0)} units in store`,
    },
    {
      label: "Avg days cover",
      value: `${Math.round(data?.avgSdcDays?.days || 0)}`,
      sub: data?.dates?.display_date ? `days · as at ${data.dates.display_date}` : "days",
    },
    ...(showDiscontinued
      ? [
          {
            label: "SOH discontinued",
            value: formatCurrency(data?.sohValueDiscontinued?.value || 0),
            sub: `${formatUnits(data?.sohValueDiscontinued?.units || 0)} units · clearance`,
            // The one metric that is always bad news gets the warning tone.
            tone: (data?.sohValueDiscontinued?.value || 0) > 0 ? signal.warn : undefined,
          },
        ]
      : []),
  ];

  return (
    // One instrument panel divided by hairlines, not six floating glass cards.
    <Box
      bg={neutral[0]}
      border="1px solid"
      borderColor={neutral[200]}
      borderRadius={radius.md}
      boxShadow={elevation.raised}
      overflow="hidden"
    >
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: tiles.length }}>
        {tiles.map((tile, i) => (
          <Kpi key={tile.label} {...tile} isLoading={loading} isLast={i === tiles.length - 1} />
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default KpiCards;
